/**
 * Premium-only: aggregate composed packages per day × payment group, then diff before/after.
 * Classic paths must not use this module.
 */

import {
  aggregateComposedKinds,
  BillablePackageKind,
  decomposeParticipantDayPackages,
  HotelPackageOptions,
  ParticipantGroupServices,
  ParticipantRoomType,
} from './participantPackageDecompose';

export type PackagePaymentState = 'COMPANY' | 'ON_SITE';

/** Minimal attendance day shape for package aggregation (form or API). */
export interface AttendanceDayForPackageDiff {
  day?: number | null;
  attending?: boolean | null;
  morning?: boolean | null;
  forenoon?: boolean | null;
  lunch?: boolean | null;
  afternoon?: boolean | null;
  dinner?: boolean | null;
  room?: ParticipantRoomType | null;
  singleroom?: boolean | null;
  doubleroom?: boolean | null;
  extras?: boolean | null;
  parking?: boolean | null;
  serviceDirectPay?: boolean | null;
  morningDirectPay?: boolean | null;
  forenoonDirectPay?: boolean | null;
  lunchDirectPay?: boolean | null;
  afternoonDirectPay?: boolean | null;
  dinnerDirectPay?: boolean | null;
  roomDirectPay?: boolean | null;
  extraDirectPay?: boolean | null;
  parkingDirectPay?: boolean | null;
}

/** Tolerates react-hook-form's DeepPartial shapes, so form state can be diffed directly. */
export interface AttendanceForPackageDiff {
  /** Participant-level DirectPay; days that leave a flag unset inherit it (see `resolveDirectPayFlag`). */
  serviceDirectPay?: boolean | null;
  roomDirectPay?: boolean | null;
  extraDirectPay?: boolean | null;
  parkingDirectPay?: boolean | null;
  days?: (AttendanceDayForPackageDiff | undefined)[] | null;
}

export interface PackageAggregateCount {
  day: number;
  paymentState: PackagePaymentState;
  kind: BillablePackageKind;
  count: number;
}

export interface PackageDiffRow {
  day: number;
  paymentState: PackagePaymentState;
  kind: BillablePackageKind;
  before: number;
  after: number;
  /** after − before; only non-zero rows are returned by `diffPackageAggregates`. */
  delta: number;
}

const _aggKey = (day: number, paymentState: PackagePaymentState, kind: BillablePackageKind) => `${day}|${paymentState}|${kind}`;

/** Extras and parking have no attendance column or SKU yet, so they must not surface as changes. */
const _NON_BILLABLE_KINDS: BillablePackageKind[] = ['EXTRAS', 'PARKING'];

const _resolveRoom = (aday: AttendanceDayForPackageDiff): ParticipantRoomType => {
  if (aday.room === 'ROOM_SINGLE' || aday.room === 'ROOM_DOUBLE' || aday.room === 'ROOM_NONE') return aday.room;
  if (aday.singleroom) return 'ROOM_SINGLE';
  if (aday.doubleroom) return 'ROOM_DOUBLE';
  return 'ROOM_NONE';
};

const _mealDirectPayField = {
  morning: 'morningDirectPay',
  forenoon: 'forenoonDirectPay',
  lunch: 'lunchDirectPay',
  afternoon: 'afternoonDirectPay',
  dinner: 'dinnerDirectPay',
} as const;

export type MealDirectPayKey = keyof typeof _mealDirectPayField;

export type MealDirectPayFlags = Pick<
  AttendanceDayForPackageDiff,
  'serviceDirectPay' | 'morningDirectPay' | 'forenoonDirectPay' | 'lunchDirectPay' | 'afternoonDirectPay' | 'dinnerDirectPay'
>;

/**
 * Per-meal on-site payment. Per-cell flags win as a set: as soon as one meal is marked, the unmarked
 * meals count as company-paid. Only a day without any per-cell mark falls back to serviceDirectPay.
 */
export function resolveMealDirectPay(aday: MealDirectPayFlags, meal: MealDirectPayKey): boolean {
  const anyPerMeal =
    !!aday.morningDirectPay || !!aday.forenoonDirectPay || !!aday.lunchDirectPay || !!aday.afternoonDirectPay || !!aday.dinnerDirectPay;
  if (anyPerMeal) return !!aday[_mealDirectPayField[meal]];
  return !!aday.serviceDirectPay;
}

const _isMealOnSite = (aday: AttendanceDayForPackageDiff, meal: MealDirectPayKey, onSiteEnabled: boolean): boolean =>
  onSiteEnabled && resolveMealDirectPay(aday, meal);

/**
 * Split one day into company vs on-site service groups (per-cell meal DirectPay).
 * When on-site mode is off, everything stays in COMPANY.
 */
export function splitParticipantDayByPayment(
  aday: AttendanceDayForPackageDiff,
  onSiteEnabled: boolean,
): { company: ParticipantGroupServices; onSite: ParticipantGroupServices } {
  const attending = !!aday.attending;
  const dayNum = aday.day ?? 0;
  const onSeminarDay = dayNum >= 0;
  // Arrival day (day < 0) keeps rooms without attending; seminar days drop rooms when not attending.
  const effectiveRoom = attending || dayNum < 0 ? _resolveRoom(aday) : 'ROOM_NONE';

  const morningOnSite = _isMealOnSite(aday, 'morning', onSiteEnabled);
  const forenoonOnSite = _isMealOnSite(aday, 'forenoon', onSiteEnabled);
  const lunchOnSite = _isMealOnSite(aday, 'lunch', onSiteEnabled);
  const afternoonOnSite = _isMealOnSite(aday, 'afternoon', onSiteEnabled);
  const dinnerOnSiteFlag = _isMealOnSite(aday, 'dinner', onSiteEnabled);
  const roomOnSite = onSiteEnabled && !!aday.roomDirectPay;
  const extraOnSite = onSiteEnabled && !!aday.extraDirectPay;
  const parkingOnSite = onSiteEnabled && !!aday.parkingDirectPay;

  const meal = (flag?: boolean | null, onSite?: boolean) => attending && onSeminarDay && !!flag && !onSite;
  const mealOnSite = (flag?: boolean | null, onSite?: boolean) => attending && onSeminarDay && !!flag && !!onSite;

  const dinnerCompany = ((attending && onSeminarDay) || dayNum < 0) && !!aday.dinner && !dinnerOnSiteFlag;
  const dinnerOnSite = ((attending && onSeminarDay) || dayNum < 0) && !!aday.dinner && dinnerOnSiteFlag;

  return {
    company: {
      morning: meal(aday.morning, morningOnSite),
      forenoon: meal(aday.forenoon, forenoonOnSite),
      lunch: meal(aday.lunch, lunchOnSite),
      afternoon: meal(aday.afternoon, afternoonOnSite),
      dinner: dinnerCompany,
      room: roomOnSite ? 'ROOM_NONE' : effectiveRoom,
      extras: attending && onSeminarDay && !!aday.extras && !extraOnSite,
      parking: attending && onSeminarDay && !!aday.parking && !parkingOnSite,
    },
    onSite: {
      morning: mealOnSite(aday.morning, morningOnSite),
      forenoon: mealOnSite(aday.forenoon, forenoonOnSite),
      lunch: mealOnSite(aday.lunch, lunchOnSite),
      afternoon: mealOnSite(aday.afternoon, afternoonOnSite),
      dinner: dinnerOnSite,
      room: roomOnSite ? effectiveRoom : 'ROOM_NONE',
      extras: attending && onSeminarDay && !!aday.extras && extraOnSite,
      parking: attending && onSeminarDay && !!aday.parking && parkingOnSite,
    },
  };
}

/** True when any DirectPay flag is set (on-site mode was used / has marks). */
export function detectOnSiteEnabledFromAttendances(attendances: (AttendanceForPackageDiff | undefined)[]): boolean {
  for (const a of attendances) {
    if (!a) continue;
    if (a.serviceDirectPay || a.roomDirectPay || a.extraDirectPay || a.parkingDirectPay) return true;
    for (const d of a.days || []) {
      if (!d) continue;
      if (
        d.serviceDirectPay ||
        d.morningDirectPay ||
        d.forenoonDirectPay ||
        d.lunchDirectPay ||
        d.afternoonDirectPay ||
        d.dinnerDirectPay ||
        d.roomDirectPay ||
        d.extraDirectPay ||
        d.parkingDirectPay
      )
        return true;
    }
  }
  return false;
}

/**
 * Aggregate greedy packages across all participants, keyed by day × payment state × kind.
 */
export function aggregateAttendancesPackages(
  attendances: (AttendanceForPackageDiff | undefined)[],
  hotel: HotelPackageOptions,
  opts?: { onSiteEnabled?: boolean },
): PackageAggregateCount[] {
  const onSiteEnabled = !!opts?.onSiteEnabled;
  const map = new Map<string, PackageAggregateCount>();

  const _add = (day: number, paymentState: PackagePaymentState, kinds: ReturnType<typeof aggregateComposedKinds>) => {
    for (const kind of Object.keys(kinds) as BillablePackageKind[]) {
      const count = kinds[kind] || 0;
      if (!count || _NON_BILLABLE_KINDS.includes(kind)) continue;
      const key = _aggKey(day, paymentState, kind);
      const existing = map.get(key);
      if (existing) existing.count += count;
      else map.set(key, { day, paymentState, kind, count });
    }
  };

  for (const attendance of attendances) {
    for (const aday of attendance?.days || []) {
      if (!aday) continue;
      const day = aday.day ?? 0;
      const { company, onSite } = splitParticipantDayByPayment(aday, onSiteEnabled);
      _add(day, 'COMPANY', aggregateComposedKinds(decomposeParticipantDayPackages(company, hotel)));
      if (onSiteEnabled) {
        _add(day, 'ON_SITE', aggregateComposedKinds(decomposeParticipantDayPackages(onSite, hotel)));
      }
    }
  }

  return Array.from(map.values()).sort((a, b) => a.day - b.day || a.paymentState.localeCompare(b.paymentState) || a.kind.localeCompare(b.kind));
}

/** Diff two aggregates; only rows with non-zero delta (per-day, no cross-day netting). */
export function diffPackageAggregates(before: PackageAggregateCount[], after: PackageAggregateCount[]): PackageDiffRow[] {
  const keys = new Set<string>();
  const beforeMap = new Map<string, number>();
  const afterMap = new Map<string, number>();

  for (const row of before) {
    const key = _aggKey(row.day, row.paymentState, row.kind);
    keys.add(key);
    beforeMap.set(key, (beforeMap.get(key) || 0) + row.count);
  }
  for (const row of after) {
    const key = _aggKey(row.day, row.paymentState, row.kind);
    keys.add(key);
    afterMap.set(key, (afterMap.get(key) || 0) + row.count);
  }

  const out: PackageDiffRow[] = [];
  for (const key of Array.from(keys)) {
    const [dayStr, paymentState, kind] = key.split('|') as [string, PackagePaymentState, BillablePackageKind];
    const b = beforeMap.get(key) || 0;
    const a = afterMap.get(key) || 0;
    const delta = a - b;
    if (delta === 0) continue;
    out.push({ day: Number(dayStr), paymentState, kind, before: b, after: a, delta });
  }

  return out.sort((x, y) => x.day - y.day || x.paymentState.localeCompare(y.paymentState) || x.kind.localeCompare(y.kind));
}

/**
 * matrixOriginal vs matrix package diff for Premium Save overview.
 */
export function diffAttendancesPackages(
  original: (AttendanceForPackageDiff | undefined)[],
  current: (AttendanceForPackageDiff | undefined)[],
  hotel: HotelPackageOptions,
  opts?: { onSiteEnabled?: boolean },
): PackageDiffRow[] {
  const onSiteEnabled = opts?.onSiteEnabled ?? (detectOnSiteEnabledFromAttendances(original) || detectOnSiteEnabledFromAttendances(current));

  return diffPackageAggregates(
    aggregateAttendancesPackages(original, hotel, { onSiteEnabled }),
    aggregateAttendancesPackages(current, hotel, { onSiteEnabled }),
  );
}
